import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TabType } from '../../types/feedback';
import { useFeedbackData } from './hooks/useFeedbackData';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorDisplay from '../../components/ErrorDisplay';
import { TabNavigation, type TabConfig } from '../../components/TabNavigation';
import MetricCard from './components/MetricCard';
import StatsBreakdown from './components/StatsBreakdown';
import UserFeedbackStates from './components/UserFeedbackStates';
import FeedbackSubmissionsTable from './components/FeedbackSubmissionsTable';
import LatestFeedbackPanel from './components/LatestFeedbackPanel';

const tabs: TabConfig<TabType>[] = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'states', label: 'User States', icon: '👥' },
    { id: 'submissions', label: 'Feedback Submissions', icon: '💬' }
];

const isValidTab = (tab: string | null): tab is TabType => {
    return tab === 'overview' || tab === 'states' || tab === 'submissions';
};

export default function UserFeedback() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<TabType>('overview');
    const { feedbackStates, feedbackSubmissions, stats, loading, error } = useFeedbackData();

    // Initialize tab from URL on mount
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (isValidTab(tabParam)) {
            setActiveTab(tabParam);
        } else if (tabParam) {
            // Invalid tab in URL, reset to overview
            setSearchParams({ tab: 'overview' }, { replace: true });
        } else {
            // No tab in URL, set default
            setSearchParams({ tab: 'overview' }, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    if (loading) {
        return <LoadingSpinner message="Loading feedback data..." />;
    }

    if (error) {
        return <ErrorDisplay message={error} />;
    }

    return (
        <div className="min-h-screen bg-gray-50 py-4 md:py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-6 md:mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">User Feedback</h1>
                </div>

                {/* Tab Navigation */}
                <TabNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />

                {/* Overview Tab */}
                {activeTab === 'overview' && stats && (
                    <div className="space-y-6">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <MetricCard
                                icon="💬"
                                label="Total Feedback"
                                value={stats.totalFeedbackSubmissions}
                                bgColor="bg-blue-100"
                            />
                            <MetricCard
                                icon="👥"
                                label="Active Users"
                                value={stats.totalActiveUsers}
                                bgColor="bg-green-100"
                            />
                            <MetricCard
                                icon="🚫"
                                label="Opted Out"
                                value={stats.totalOptedOutUsers}
                                bgColor="bg-red-100"
                            />
                            <MetricCard
                                icon="⚡"
                                label="Avg Actions/User"
                                value={stats.averageActionsPerUser.toFixed(1)}
                                bgColor="bg-purple-100"
                            />
                        </div>

                        {/* Feedback by Source and Flow */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <StatsBreakdown
                                title="Feedback by Source"
                                data={stats.feedbackBySource}
                            />
                            <StatsBreakdown
                                title="Feedback by Flow"
                                data={stats.feedbackByFlow}
                            />
                        </div>

                        <LatestFeedbackPanel feedbackEntries={feedbackSubmissions.slice(0, 20)} />
                    </div>
                )}

                {/* User States Tab */}
                {activeTab === 'states' && (
                    <UserFeedbackStates feedbackStates={feedbackStates} />
                )}

                {/* Feedback Submissions Tab */}
                {activeTab === 'submissions' && (
                    <FeedbackSubmissionsTable feedbackSubmissions={feedbackSubmissions} />
                )}
            </div>
        </div>
    );
} 